import LayoutContent from '@components/LayoutContent'
import { useRNApp } from '@libs/RNApp'
import React, { useRef, useEffect, useState } from 'react'
import UserProfile from './UserInfo'
import { Tab, TabPanel, Tabs } from './TabSection'
import { useRouter } from 'next/router'
import { compose, connect, setStatic } from '@hocs'
import {
  getInitialUserPostList,
  setUserPostsType,
  setUserPostsClas,
  setUserPostsMode,
  getInitialUserCollectionList,
  setUserCollectionsType,
  setUserCollectionsClas,
  setUserCollectionsMode,
} from '@app-core/profile/actions'
import { getUserInfoByGql, setIsGuest, setIsMeRoute, setIsSelf } from '@app-core/profile/actions'
import { bindActionCreators } from 'redux'
import { getApolloClient } from '@libs/getApolloClient'
import { gql } from '@apollo/client'
import UserArticles from './UserArticles'
import { useSelector } from 'react-redux'
import { NotFoundError } from '@features/errors/NotFoundError'
import UserCollections from './UserCollections'
import { getUrlSearchParams } from '@libs/url-utils'
import { trackUserProfileViewEvent } from '@libs/eventTracking/profile/user-profile.view'
import ProfileHead from './ProfileHead'

const Profile = () => {
  const { isApp } = useRNApp()
  const router = useRouter()
  const hasSentProfileView = useRef(false)
  const mid = useSelector<any, any>((state) => state.profile.getIn(['userInfo', 'data', 'mid']))

  const defaultTabType = !router.pathname.includes('show/topic') ? 'article' : 'collections'
  const [tabType, setTabType] = useState(defaultTabType)

  const handleProfileViewEvent = () => {
    if (typeof window === 'undefined') return
    if (hasSentProfileView.current) return

    const fromPage = (getUrlSearchParams(window.location)?.fromPage ?? {}) as Record<string, any>
    trackUserProfileViewEvent({
      currentPage: {
        pageName: 'user-profile',
      },
      fromPage,
    })
    hasSentProfileView.current = true
  }

  const handleOnChange = (e, newValue) => {
    if (newValue === tabType) return
    setTabType(newValue)
    router.push({
      pathname: router.pathname.includes('show/topic')
        ? router.pathname.replace('/show/topic', '')
        : router.pathname + '/show/topic',
      query: { ...router.query },
    })
  }

  useEffect(() => {
    handleProfileViewEvent()
  }, [])

  if (!mid) {
    return <NotFoundError isApp={isApp} />
  }

  return (
    <>
      <ProfileHead />
      <LayoutContent disabledDefaultHeader isApp={isApp} noTopAd>
        <UserProfile />
        <Tabs value={tabType} onChange={handleOnChange}>
          <Tab label="文章" value="article" />
          <Tab label="收藏" value="collections" />
        </Tabs>
        <TabPanel value="article" index={0} curValue={tabType}>
          <UserArticles />
        </TabPanel>
        <TabPanel value="collections" index={1} curValue={tabType}>
          <UserCollections />
        </TabPanel>
      </LayoutContent>
    </>
  )
}

const mapStateToProps = (state) => state
const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch)
const mergeProps = (state, dispatchProps, ownProps) => {
  const { getIndexPostList, getInitialPostList } = dispatchProps

  return {
    ...state,
    ...ownProps,

    getIndexPostList,
    getInitialPostList,
  }
}

const ME = gql`
  query ME {
    me {
      id
      name
      mid
      uid
      des
      views
      photoUrl
      followers
      following
      numberOfArticles
      roles
      createdAt
      updatedAt
    }
  }
`

const USER = gql`
  query User($mid: String!, $isUserFollowingMid: String!) {
    user(mid: $mid) {
      id
      name
      mid
      uid
      des
      views
      photoUrl
      followers
      following
      numberOfArticles
      roles
      isUserFollowing(mid: $isUserFollowingMid)
      createdAt
      updatedAt
    }
  }
`

const getGuestMidByToken = (token) => {
  if (!token) return ''
  return token.split('.', 2).join('-')
}

export default compose(
  setStatic('getInitialProps', async (ctx) => {
    const { req, store, query } = ctx
    const { mid: routeMid } = query
    const isMeRoute = !routeMid || routeMid === 'undefined'
    const isCsr = !req

    // tab 在 csr router push 的時候，不再次進行 getInitialProps 邏輯
    if (isCsr) {
      return {}
    }

    // -- 拿取 profile 資料 --
    const client = getApolloClient(ctx)
    let getUserInfoFunction = null
    let getUserInfoFunctionType = null

    /**
     * 是否登入以及 token 狀態目前尚未遷移至 graph
     * 目前拿 currentUser 的狀態，是打 v1 api 取得
     */

    const guestToken = await store.getState().currentUser.getIn(['userToken'])
    const currentUserMid = await store.getState().currentUser.getIn(['userInfo', 'mid'])
    const isGuest = !currentUserMid

    if (isMeRoute) {
      if (isGuest) {
        const getEmptyUserInfo = async () => ({
          data: {
            guest: {
              id: null,
              name: '訪客',
              mid: getGuestMidByToken(guestToken),
              uid: null,
              des: '',
              photoUrl: '',
              followers: 0,
              following: 0,
              views: 0,
              roles: [],
            },
          },
          error: null,
        })
        getUserInfoFunction = getEmptyUserInfo
        getUserInfoFunctionType = 'guest'
      } else {
        const getGqlMeUserInfo = async () => await client.query({ query: ME })
        getUserInfoFunction = getGqlMeUserInfo
        getUserInfoFunctionType = 'me'
      }
    } else {
      const getGqlUserInfo = async () =>
        await client.query({
          query: USER,
          variables: {
            mid: routeMid,
            isUserFollowingMid: isGuest ? '' : currentUserMid,
          },
        })
      getUserInfoFunction = getGqlUserInfo
      getUserInfoFunctionType = 'user'
      await store.dispatch(setIsSelf({ isSelf: routeMid === currentUserMid }))
    }

    await store.dispatch(getUserInfoByGql({ getUserInfoFunction, getUserInfoFunctionType }))
    await store.dispatch(setIsMeRoute({ isMeRoute }))
    await store.dispatch(setIsGuest({ isGuest }))

    // -- 拿初始文章 --
    await store.dispatch(setUserPostsType({ type: 'user' }))
    await store.dispatch(setUserPostsClas({ clas: isMeRoute ? currentUserMid : routeMid }))
    await store.dispatch(setUserPostsMode({ mode: 'new' }))
    await store.dispatch(
      getInitialUserPostList({
        clas: isMeRoute ? currentUserMid : routeMid,
        mode: 'new',
        type: 'user',
      }),
    )

    // -- 拿收藏文章 --
    await store.dispatch(setUserCollectionsType({ type: 'user' }))
    await store.dispatch(setUserCollectionsClas({ clas: isMeRoute ? currentUserMid : routeMid }))
    await store.dispatch(setUserCollectionsMode({ mode: 'new' }))
    await store.dispatch(
      getInitialUserCollectionList({
        clas: isMeRoute ? currentUserMid : routeMid,
        mode: 'new',
        type: 'user',
      }),
    )

    return {}
  }),
  connect(mapStateToProps, mapDispatchToProps, mergeProps),
)(Profile)
